import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useEffect, useRef } from 'react';

import { useBlackboxVersion } from '@feathr/extender/hooks';
import { useAccount, useUser } from '@feathr/extender/state';

import { useDonationFormBuilder } from '../useDonationFormBuilder';
import { getEditorUrl } from './EditorIframe.utils';

import * as styles from './EditorIframe.css';

interface IEditorConfigMessage {
  type: 'feathr:editor:config';
  config: Record<string, unknown>;
  isDirty: boolean;
}

function isEditorConfigMessage(data: unknown): data is IEditorConfigMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'feathr:editor:config'
  );
}

function EditorIframe(): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { donationForm, setEditorConfig } = useDonationFormBuilder();

  // Determine if user is a developer (same logic as Development & QA nav link)
  const account = useAccount();
  const user = useUser();
  const { isOnProduction } = useBlackboxVersion();
  const showDevelopmentNavUserSetting = user.getSetting('show_dev_nav') ?? false;
  const isDeveloper = !isOnProduction && (account.isTestAccount || showDevelopmentNavUserSetting);

  const { url: editorUrl, origin: editorOrigin } = getEditorUrl(isDeveloper);

  const sendConfigToEditor = useCallback((): void => {
    if (!iframeRef.current?.contentWindow) {
      return;
    }

    const config = toJS(donationForm.get('config') ?? {});

    iframeRef.current.contentWindow.postMessage(
      {
        type: 'feathr:editor:init',
        config,
        formId: donationForm.id,
      },
      editorOrigin,
    );
  }, [donationForm, editorOrigin]);

  const handleMessage = useCallback(
    (event: MessageEvent): void => {
      if (event.origin !== editorOrigin) {
        return;
      }

      if (isEditorConfigMessage(event.data)) {
        setEditorConfig(event.data.config, event.data.isDirty);
      }
    },
    [editorOrigin, setEditorConfig],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    const handleLoad = (): void => {
      sendConfigToEditor();
    };

    iframe.addEventListener('load', handleLoad);

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [sendConfigToEditor]);

  return (
    <div className={styles.root}>
      <iframe
        className={styles.iframe}
        ref={iframeRef}
        src={editorUrl}
        title={'Donation Form Editor'}
      />
    </div>
  );
}

export default observer(EditorIframe);
